// Libraries
import React from 'react';

// Supermove
import {Styled, Space, DataLoader} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useDrawer, useQuery, useEffect, useToast, useResponsive} from '@supermove/hooks';
import {AccountGrantModel, OrganizationModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import DropdownButton from '@shared/design/components/Button/DropdownButton';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import Text from '@shared/design/components/Text';
import LoadingToast from '@shared/design/components/Toast/LoadingToast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import CreateAccountGrantForm from '@shared/modules/AccountGrant/forms/CreateAccountGrantForm';
import CommunicationAccountsTable from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/CommunicationAccountsTable';
import EditBranchAccountsDrawer from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/EditBranchAccountsDrawer';
import {
  getEditAccess,
  EditAccessType,
} from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/getEditAccess';
import useConnectAccountGrant from 'modules/Organization/Settings/Communication/hooks/useConnectAccountGrant';

const PageContainer = Styled.View`
  flex: 1;
  background-color: ${colors.gray.background};
`;

const LoadingIndicator = Styled.Loading`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const AccountsContentContainer = Styled.View`
  flex: 1;
  padding-horizontal: 24px;
`;

const HeadingTextContainer = Styled.View`
  flex: 1;
`;

const AccountsContentHeaderContainer = Styled.View`
  padding-horizontal: 24px;
  padding-vertical: 24px;
  z-index: 1;
`;

const getManageAccountForBranchesTooltip = (
  accountGrants: AccountGrantModel[],
  organizations: OrganizationModel[],
) => {
  if (accountGrants.length === 0) {
    return 'No accounts found. Connect an account to assign it to specific branches.';
  }
  // The user can switch from one to another
  if (accountGrants.length > 1) {
    return undefined;
  }
  const primaryGrant = accountGrants.find((grant) => grant.isPrimary);
  const allUsingPrimaryGrant =
    primaryGrant &&
    organizations
      .filter((organization) => organization.isDeleted === false)
      .every((organization) =>
        organization.accountGrantOrganizationAssignments?.some(
          (assignment) => assignment.accountGrantId.toString() === primaryGrant.id,
        ),
      );

  if (allUsingPrimaryGrant) {
    return 'All branches are currently using the primary account as default sender account. Connect a new account to assign it to specific branches.';
  }
  return undefined;
};

const AccountsContentHeader = ({
  onPressEditBranchAccounts,
  accountGrants,
  organizations,
  handleConnectAccount,
  isLoading,
  editAccess,
}: {
  onPressEditBranchAccounts: () => void;
  accountGrants: AccountGrantModel[];
  organizations: OrganizationModel[];
  handleConnectAccount: () => void;
  isLoading: boolean;
  editAccess: EditAccessType;
}) => {
  const responsive = useResponsive();
  const manageAccountForBranchesTooltip = getManageAccountForBranchesTooltip(
    accountGrants,
    organizations,
  );

  return (
    <AccountsContentHeaderContainer>
      <Row style={{justifyContent: 'space-between'}}>
        <HeadingTextContainer>
          <Text.Heading2>Accounts</Text.Heading2>
          <Space height={8} />
          <Text.Body>Manage connected company accounts.</Text.Body>
        </HeadingTextContainer>
        <Space width={24} />
        {editAccess === 'EDIT' ? (
          <DropdownButton
            isSubmitting={isLoading}
            text={'Actions'}
            menuPosition={'RIGHT'}
            ButtonComponent={responsive.desktop ? undefined : TertiaryButton}
            menuWidth={250}
            actions={[
              {
                text: 'Connect company account',
                onPress: handleConnectAccount,
              },
              {
                text: 'Manage default for branches',
                tooltip: manageAccountForBranchesTooltip,
                isDisabled: !!manageAccountForBranchesTooltip,
                onPress: onPressEditBranchAccounts,
              },
            ]}
            isResponsive
          />
        ) : null}
      </Row>
    </AccountsContentHeaderContainer>
  );
};

const AccountsContent = ({
  organization,
  loading,
  refetch,
  viewer,
}: {
  organization: OrganizationModel;
  loading: boolean;
  refetch: () => void;
  viewer: UserModel;
}) => {
  const editBranchAccountsDrawer = useDrawer({name: 'Edit Branch Accounts Drawer'});
  const createAccountGrantForm = CreateAccountGrantForm.new({
    kind: 'ORGANIZATION',
    provider: 'NYLAS',
    organizationId: organization.id,
  });

  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Account connected.',
  });
  const {connectAccountGrant, submitting: connectingAccount} = useConnectAccountGrant({
    createAccountGrantForm,
    onSuccess: () => {
      refetch();
      successToast.handleToast();
    },
    refetchQueries: ['OrganizationSettingsCommunicationAccountsTable'],
    redirectPath: '/settings/communication/accounts',
  });
  const handleUpdateAccountGrant = (accountGrant: AccountGrantModel) => {
    connectAccountGrant(accountGrant.id);
  };

  const loadingToast = useToast({
    ToastComponent: LoadingToast,
    message: 'Connecting account.',
    duration: Infinity,
  });

  useEffect(() => {
    if (connectingAccount) {
      loadingToast.handleToast();
    } else if (loadingToast.toastElementId) {
      loadingToast.dismissToast();
    }
  }, [connectingAccount]);

  const editAccess = getEditAccess(viewer, organization);

  return (
    <React.Fragment>
      <AccountsContentHeader
        onPressEditBranchAccounts={editBranchAccountsDrawer.handleOpen}
        accountGrants={organization.activeAccountGrants}
        handleConnectAccount={connectAccountGrant}
        organizations={organization.allOrganizations}
        isLoading={connectingAccount}
        editAccess={editAccess}
      />
      <AccountsContentContainer>
        <CommunicationAccountsTable
          handleUpdateAccountGrant={handleUpdateAccountGrant}
          editAccess={editAccess}
          handleConnectAccount={connectAccountGrant}
        />
      </AccountsContentContainer>
      <EditBranchAccountsDrawer drawer={editBranchAccountsDrawer} refetch={refetch} />
    </React.Fragment>
  );
};

const CommunicationAccountsPage = () => {
  const {data, loading, refetch} = useQuery<{viewer: UserModel}>(CommunicationAccountsPage.query, {
    fetchPolicy: 'cache-and-network',
  });

  return (
    <React.Fragment>
      <PageContainer>
        <DataLoader loading={loading} data={data} as={LoadingIndicator}>
          {({loadedData}) => (
            <AccountsContent
              organization={loadedData.viewer.viewingOrganization}
              loading={loading}
              refetch={refetch}
              viewer={loadedData.viewer}
            />
          )}
        </DataLoader>
      </PageContainer>
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
CommunicationAccountsPage.query = gql`
  ${getEditAccess.fragment}

  query CommunicationAccountsPage {
    ${gql.query}
    viewer {
      id
      ...getEditAccess_user
      viewingOrganization {
        id
        allOrganizations {
          id
          isDeleted
          accountGrantOrganizationAssignments {
            id
            accountGrantId
            organizationId
          }
        }
        activeAccountGrants {
          id
          isPrimary
        }
        ...getEditAccess_organization
      }
    }
  }
`;

export default CommunicationAccountsPage;
